import React from 'react';

interface ShotItem {
  emoji: string;
  name: string;
  duration: string;
  desc: string;
  tips: string;
}

const SHOTS: ShotItem[] = [
  { emoji:'📦', name:'产品展示', duration:'5–8秒', desc:'完整展示产品外观，让观众知道这是什么产品。360度旋转或固定角度均可，确保产品清晰可见。', tips:'背景干净，光线充足，产品居中放置' },
  { emoji:'🔍', name:'产品细节', duration:'5–8秒', desc:'1-2个关键部位特写。如：锁孔结构、锁扣材质、品牌标识、安全认证标志等差异化卖点。', tips:'聚焦卖点，不要拍无关细节' },
  { emoji:'🔧', name:'安装过程', duration:'5–8秒', desc:'展示如何将产品安装到设备上。这是使用演示的核心起点，动作要慢，步骤要清晰。', tips:'动作放慢！每一步都要完整' },
  { emoji:'🔒', name:'锁定动作（核心）', duration:'5–8秒', desc:'**最重要的镜头** — 展示产品如何锁定/锁住设备。这是LOTO安全锁的核心价值所在。', tips:'最核心镜头，多拍几遍选最好的' },
  { emoji:'✅', name:'使用完成效果', duration:'5–8秒', desc:'展示锁定后的最终效果：设备被安全锁定、无法操作的状态。给用户"安全感"。', tips:'停留2秒让客户看清结果' }
];

const RULES = [
  { icon:'⏱️', text:'<strong>每个镜头 5-8 秒</strong>，不要太短也不要太长' },
  { icon:'📱', text:'<strong>必须同时拍摄</strong>：竖屏(9:16) + 横屏(16:9)' },
  { icon:'📷', text:'<strong>使用三脚架</strong>固定拍摄，严禁手持抖动' },
  { icon:'🐢', text:'<strong>操作动作要慢</strong>，宁可多剪不要缺素材' },
  { icon:'💡', text:'<strong>光线充足</strong>，自然光或补光灯均可，避免阴影遮挡' },
  { icon:'🔇', text:'<strong>环境安静</strong>，避免背景杂音和人员走动干扰' }
];

const ShootingStandards: React.FC = () => (
  <div className="app-main">
    <div style={{ marginBottom:'26px' }}>
      <h1 style={{ fontSize:'24px', fontWeight:800, color:'var(--text)', marginBottom:'5px' }}>📋 拍摄标准</h1>
      <p style={{ fontSize:'14px', color:'var(--text-muted)', lineHeight:1.6 }}>
        每个产品只需拍摄 <strong style={{ color:'var(--primary)' }}>5个核心镜头</strong>，简单清晰，美工一看就懂。
      </p>
    </div>

    {/* 5个镜头 */}
    <section className="card" style={{ marginBottom:'24px' }}>
      <h2 className="section-title"><span className="icon">🎬</span> 核准拍摄内容（5个镜头）</h2>
      <p style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'18px' }}>按顺序拍摄，每个镜头都有明确的时长和要求。</p>
      <div className="shot-grid">
        {SHOTS.map((s, i) => (
          <div className="shot-card" key={i}>
            <span className="shot-number">{i+1}</span>
            <div className="shot-header">
              <span className="shot-emoji">{s.emoji}</span>
              <span className="shot-name">{s.name}<span className="shot-duration">{s.duration}</span></span>
            </div>
            <p className="shot-desc">{s.desc}</p>
            <div className="shot-tips">💡 {s.tips}</div>
          </div>
        ))}
      </div>
    </section>

    {/* 拍摄规则 */}
    <section style={{ marginBottom:'24px' }}>
      <div className="card">
        <h2 className="section-title"><span className="icon">⚙️</span> 拍摄要求</h2>
        <div className="rules-grid">
          {RULES.map((r, i) => (
            <div className="rule-item" key={i}>
              <span className="rule-check">{r.icon}</span>
              <span className="rule-text" dangerouslySetInnerHTML={{ __html:r.text }} />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* 剪辑规格 */}
    <section>
      <h2 className="section-title" style={{ marginBottom:'14px' }}><span className="icon">✂️</span> 剪辑规格</h2>
      <div className="specs-compare">
        <div className="spec-card made-in-china">
          <span className="spec-label">📱 中国制造网</span>
          <div className="spec-detail-row"><span className="spec-key">画面比例</span><span className="spec-val">竖屏 9:16</span></div>
          <div className="spec-detail-row"><span className="spec-key">视频时长</span><span className="spec-val">30 – 45 秒</span></div>
          <div className="spec-detail-row"><span className="spec-key">文件格式</span><span className="spec-val">MP4 (H.264)</span></div>
          <div className="spec-detail-row"><span className="spec-key">内容顺序</span><span className="spec-val">展示 → 使用过程 → 结果</span></div>
          <div className="spec-detail-row"><span className="spec-key">分辨率</span><span className="spec-val">1080×1920</span></div>
        </div>
        <div className="spec-card website">
          <span className="spec-label">🌐 独立站 / 海外平台</span>
          <div className="spec-detail-row"><span className="spec-key">画面比例</span><span className="spec-val">横屏 16:9</span></div>
          <div className="spec-detail-row"><span className="spec-key">视频时长</span><span className="spec-val">40 – 60 秒</span></div>
          <div className="spec-detail-row"><span className="spec-key">文件格式</span><span className="spec-val">MP4 (H.264)</span></div>
          <div className="spec-detail-row"><span className="spec-key">内容顺序</span><span className="spec-val">介绍 → 使用演示</span></div>
          <div className="spec-detail-row"><span className="spec-key">分辨率</span><span className="spec-val">1920×1080</span></div>
        </div>
      </div>
    </section>

    {/* 正误对比 */}
    <div className="alert alert-warning" style={{ marginTop:'22px' }}>
      <span>⚠️</span>
      <div>
        <strong>常见错误提醒：</strong>
        <ul style={{ marginTop:'7px', paddingLeft:'18px', color:'#b45309', fontSize:'12px', lineHeight:1.7, margin:0 }}>
          <li>❌ 不要只拍产品静态图 + 音乐 — 这不是"使用演示"</li>
          <li>❌ 不要用花哨转场特效 — 保持专业简洁风格</li>
          <li>❌ 不要拍得太快 — 客户看不懂你的操作就等于白拍</li>
          <li>❌ 不要忘记拍竖版 — 中国制造网需要9:16</li>
        </ul>
      </div>
    </div>

    <footer className="app-footer">
      <p>© 2026 Prolockey 洛科安全防护用品有限公司 · 视频拍摄任务管理系统 · 内部使用</p>
    </footer>
  </div>
);

export default ShootingStandards;
